import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  transformedValue,
  type FormValueControl,
  type ValidationError,
} from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { CursorInputState, RangeCursorInputState } from './number-field.models';
import {
  checkPercentExceeds,
  cleanNumber,
  formatForDisplay,
  formatNumberWithSpaces,
  isDigit,
  isSeparator,
  MAX_PERCENT,
  MINUS_SIGN,
  modifySpacesAdd,
  modifySpacesRemove,
  NumberValidationErrorKind,
  processDeleteBackward,
  processDeleteForward,
  processSymbol,
  removeTrailingZeros,
  validateNumber,
} from './_core/number-input';
import { InputEventType } from '../../../constants/input-event-types.constants';

type NumberFieldValue = string | number | null;

/** Past any length the field can hold, so a focus selects the whole text. */
const SELECT_ALL_RANGE_END = 1000;

interface NumberFieldErrorMessage {
  key: string;
  value?: unknown;
}

/**
 * Masked numeric text field: groups thousands with spaces while typing and emits the cleaned
 * number string. Range and length checks are parse errors, so they reach the bound field without
 * any validator on the form side; `required` comes from the form schema.
 */
@Component({
  selector: 'fr8bot-ui-number-field',
  imports: [TranslatePipe, NgClass],
  templateUrl: './number-field.component.html',
  styleUrl: './number-field.component.scss',
  host: { '[attr.focused]': 'focused() || null' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberFieldComponent
  extends UiFieldControl
  implements FormValueControl<NumberFieldValue>
{
  readonly value = model<NumberFieldValue>(null);

  readonly isInvalid = input(false);
  readonly label = input<string>();
  readonly placeholder = input('');
  readonly size = input<'small' | 'medium'>('medium');
  readonly leftText = input<string>();
  readonly rightText = input<string>();

  // Not `maxLength`: `[formField]` owns that input name.
  readonly maxIntegerDigits = input<number>();
  readonly minInputValue = input<string | number>();
  readonly maxInputValue = input<string | number>();
  readonly allowNegative = input(false);
  readonly zeroAble = input(false);
  // When false, an empty field is shown as 0 on blur unless `fractional` > 0.
  readonly skipZero = input(false);
  readonly isPercent = input(false);
  readonly fractional = input(0);
  readonly fractionalMin = input(0);

  readonly blurEvent = output<string>();
  readonly focusEvent = output<void>();

  protected readonly focused = signal(false);

  private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');

  // Set only while the revalidation effect below re-parses the current text: `parse` then
  // reports errors without writing the model, which a `computed()` can't do.
  private revalidating = false;

  protected readonly rawValue = transformedValue(this.value, {
    parse: (raw: string) =>
      this.revalidating
        ? { error: this.validateRaw(raw) }
        : { value: this.keepIfUnchanged(cleanNumber(raw)), error: this.validateRaw(raw) },
    format: (value) => this.formatForDisplay(value === null ? '' : String(value)),
  });

  protected readonly isFilled = computed(() => {
    const value = this.value();
    return value !== null && value !== undefined && value.toString().length > 0;
  });

  protected readonly errorMessage = computed<NumberFieldErrorMessage | null>(() => {
    const error = this.touched() ? this.errors()[0] : undefined;
    return error ? this.toMessage(error) : null;
  });

  protected readonly isInvalidShown = computed(() => !!this.errorMessage() || this.isInvalid());

  private previousFractional = 0;

  constructor() {
    super();

    effect(() => {
      const fractional = this.fractional();

      untracked(() => {
        if (fractional < this.previousFractional) {
          this.trimFraction(fractional);
        }

        this.previousFractional = fractional;
      });
    });

    // Parse errors are only produced by a write, so a value that arrives already out of range
    // — or a limit that moves past the current value — would leave the field valid. Re-parsing
    // the same text refreshes the errors without writing the model.
    effect(() => {
      this.value();
      this.minInputValue();
      this.maxInputValue();
      this.maxIntegerDigits();
      this.isPercent();
      this.zeroAble();
      this.required();

      untracked(() => {
        this.revalidating = true;
        this.rawValue.set(this.rawValue());
        this.revalidating = false;
      });
    });
  }

  private get element(): HTMLInputElement {
    return this.inputRef().nativeElement;
  }

  protected onFocus(): void {
    this.focused.set(true);
    this.focusEvent.emit();
    setTimeout(() => this.element.setSelectionRange(0, SELECT_ALL_RANGE_END));

    if (this.fractionalMin() > this.fractional()) {
      const [intPart = '', fracPart] = this.element.value.split('.');

      if (fracPart) {
        this.element.value =
          this.fractional() === 0
            ? intPart
            : intPart + '.' + fracPart.substring(0, this.fractional());
      }
    }
  }

  protected onBlur(): void {
    this.focused.set(false);
    this.element.value = this.formatForDisplay(this.element.value);
    this.touch.emit();
    this.commit();
    this.blurEvent.emit(removeTrailingZeros(this.element.value.replace(/ /g, '')));
  }

  private commit(): void {
    this.rawValue.set(this.element.value);
  }

  private trimFraction(fractional: number): void {
    const [intPart = '', fracPart] = this.element.value.split('.');

    this.element.value =
      fractional === 0 || fracPart === undefined
        ? intPart
        : intPart + '.' + fracPart.substring(0, fractional);
    this.commit();
  }

  // Blurring re-parses the text: without this a bound `120` would come back as `"120"` and read
  // as an edit to anything comparing the model.
  private keepIfUnchanged(cleaned: string): NumberFieldValue {
    const current = untracked(this.value);
    const currentText = current === null || current === undefined ? '' : cleanNumber(`${current}`);

    return cleaned === currentText ? current : cleaned;
  }

  private toMessage(error: ValidationError): NumberFieldErrorMessage {
    if (error.message) {
      return { key: error.message };
    }

    switch (error.kind) {
      case NumberValidationErrorKind.Zero:
        return { key: 'common.numberInput.zeroNotAllowed' };
      case NumberValidationErrorKind.Min:
        return { key: 'common.numberInput.minValue', value: this.minInputValue() };
      case NumberValidationErrorKind.Max:
        return { key: 'common.numberInput.maxValue', value: this.maxInputValue() };
      case NumberValidationErrorKind.MaxLength:
        return { key: 'common.numberInput.maxLength', value: this.maxIntegerDigits() };
      default:
        return { key: `errors.validationErrors.${error.kind}` };
    }
  }

  private formatForDisplay(input: string): string {
    return formatForDisplay(input, {
      fractional: this.fractional(),
      fractionalMin: this.fractionalMin(),
      skipZero: this.skipZero(),
    });
  }

  private validateRaw(raw: string): ValidationError.WithoutFieldTree | undefined {
    return validateNumber(raw, {
      required: this.required(),
      zeroAble: this.zeroAble(),
      isPercent: this.isPercent(),
      min: this.minInputValue(),
      max: this.maxInputValue(),
      maxIntegerDigits: this.maxIntegerDigits(),
    });
  }

  /*
   * Keystroke handling: every edit is applied to the element by hand (the native one is
   * cancelled) so the thousands spaces and the cursor position stay consistent.
   */

  protected onBeforeInput(event: InputEvent): void {
    event.preventDefault();

    const element = this.element;
    let cursorStart = element.selectionStart!;
    let cursorEnd = element.selectionEnd!;

    if (
      event.inputType === InputEventType.DeleteContentBackward ||
      event.inputType === InputEventType.DeleteContentForward
    ) {
      const backward = event.inputType === InputEventType.DeleteContentBackward;
      const edge = backward ? 0 : element.value.length;

      if (cursorStart === cursorEnd && cursorStart === edge) return;

      if (cursorStart === 0 && cursorEnd === element.value.length) {
        element.value = '';
        this.commit();
        return;
      }

      if (
        cursorStart === cursorEnd &&
        cursorStart === (backward ? 1 : 0) &&
        element.value.includes('-')
      ) {
        element.value = element.value.substring(1);
        element.setSelectionRange(0, 0);
        this.commit();
        return;
      }

      const hasMinus = element.value.includes('-');
      const prevState: RangeCursorInputState = {
        value: element.value.replace('-', ''),
        cursorStart: cursorStart - (hasMinus ? 1 : 0),
        cursorEnd: cursorEnd - (hasMinus ? 1 : 0),
      };
      const withoutSpaces = modifySpacesRemove(prevState);
      const nextState = modifySpacesAdd(
        backward ? processDeleteBackward(withoutSpaces) : processDeleteForward(withoutSpaces),
      );

      this.applyState(nextState, hasMinus);
    } else if (event.inputType === InputEventType.DeleteByCut) {
      if (cursorStart === 0 && cursorEnd === element.value.length) {
        navigator.clipboard.writeText(element.value.replaceAll(' ', ''));
        element.value = '';
        this.commit();
      }
    } else if (event.inputType === InputEventType.InsertText) {
      if (
        event.data &&
        event.data !== MINUS_SIGN &&
        !isSeparator(event.data) &&
        !isDigit(event.data)
      )
        return;

      if (cursorStart === 0 && cursorEnd === element.value.length) {
        element.value = '';
        element.setSelectionRange(0, 0);
        cursorStart = 0;
        cursorEnd = 0;
      }

      if (cursorStart !== cursorEnd) return;
      if (element.value.includes('-') && cursorStart === 0) return;

      if (event.data === MINUS_SIGN) {
        if (this.allowNegative() && cursorStart === 0 && !element.value.includes('-')) {
          element.value = '-' + element.value;
          element.setSelectionRange(1, 1);
          this.commit();
        }
        return;
      }

      const hasMinus = element.value.includes('-');
      const prevState: CursorInputState = {
        value: element.value.replace('-', ''),
        cursorIndex: element.selectionStart! - (hasMinus ? 1 : 0),
      };

      if (event.data && event.data.length === 1) {
        const withoutSpaces = modifySpacesRemove(prevState);
        const nextState = modifySpacesAdd(
          checkPercentExceeds(
            withoutSpaces,
            processSymbol(event.data, withoutSpaces, {
              fractional: this.fractional(),
              maxIntegerDigits: this.maxIntegerDigits(),
              isPercent: this.isPercent(),
            }),
            this.isPercent(),
          ),
        );

        this.applyState(nextState, hasMinus);
      }
    } else if (event.inputType === InputEventType.InsertFromPaste) {
      if (!event.data) return;

      const raw = event.data.replaceAll(' ', '').replace(',', '.');
      const match = raw.match(/^-?\d+(?:\.(\d+))?$/);

      if (match) {
        const [rawIntPart = ''] = raw.split('.');
        const intPart = rawIntPart.replace('-', '');
        const fracPart = match[1] ?? '';
        const maxIntegerDigits = this.maxIntegerDigits() ?? Infinity;

        if (
          ((!this.isPercent() && intPart.length <= maxIntegerDigits) ||
            (this.isPercent() && parseInt(intPart) <= MAX_PERCENT)) &&
          fracPart.length <= this.fractional()
        ) {
          element.value = formatNumberWithSpaces(this.allowNegative() ? raw : raw.replace('-', ''));
          this.commit();
        }
      }
    }
  }

  private applyState(state: CursorInputState, hasMinus: boolean): void {
    const offset = hasMinus ? 1 : 0;

    this.element.value = (hasMinus ? '-' : '') + state.value;
    this.element.setSelectionRange(state.cursorIndex + offset, state.cursorIndex + offset);
    this.commit();
  }
}
